import { Entity } from '@backstage/catalog-model';
import Box from '@material-ui/core/Box';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import LinearProgress from '@material-ui/core/LinearProgress';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import ExternalLinkIcon from '@material-ui/icons/Launch';
import qs from 'qs';
import React from 'react';
import { useProjectName } from '../../useProjectName';
import { WorkflowRunStatus } from '../WorkflowRunStatus';
import { useWorkflowRunsDetails } from './useWorkflowRunsDetails';
import { Breadcrumbs, Link, WarningPanel } from '@backstage/core-components';
import { getLocation } from '../../useLocation';
import { calculateBuildTimeDuration } from '../utils/calculateBuildTimeDuration';
import Grid from '@material-ui/core/Grid';
import { WorkflowRunLogs } from './WorkflowRunLogs';

const useStyles = makeStyles(theme => ({
  root: {
    margin: theme.spacing(2),
  },
  title: {
    padding: theme.spacing(1, 0, 2, 0),
  },
  table: {
    padding: theme.spacing(1),
  },
  nestedTable: {
    marginBottom: 0,
  },
  accordionDetails: {
    padding: 0,
  },
  button: {
    order: -1,
    marginRight: 0,
    marginLeft: '-20px',
  },
  externalLinkIcon: {
    fontSize: 'inherit',
    verticalAlign: 'bottom',
  },
  borderedCell: {
    border: `1px solid ${theme.palette.divider}`,
  },
  substitutionsTable: {
    '& th, & td': {
      border: `1px solid ${theme.palette.divider}`,
    },
  },
  stepsTable: {
    '& th, & td': {
      border: `1px solid ${theme.palette.divider}`,
    },
  },
}));

export const WorkflowRunDetails = (props: { entity: Entity }) => {
  const { value: projectName, loading, error } = useProjectName(props.entity);
  const [projectId] = (projectName ?? '/').split('/');
  const location = getLocation(props.entity);

  const details = useWorkflowRunsDetails(projectId, location);
  const [selectedTab, setSelectedTab] = React.useState(0);

  const handleChange = (_event: React.ChangeEvent<{}>, newValue: number) => {
    setSelectedTab(newValue);
  };

  const classes = useStyles();
  if (error) {
    return (
      <WarningPanel title="Error:">
        Failed to load build, {error.message}.
      </WarningPanel>
    );
  } else if (loading) {
    return <LinearProgress />;
  } else if (details.value?.logUrl === undefined) {
    return <LinearProgress />;
  }

  const serviceAccount = qs.parse(new URL(details.value?.logUrl).search, {
    ignoreQueryPrefix: true,
  }).project;

  const totalBuildTime = details.value?.timing?.BUILD
    ? calculateBuildTimeDuration(
        details.value.timing.BUILD.startTime,
        details.value.timing.BUILD.endTime,
      )
    : 'N/A';

  return (
    <div className={classes.root}>
      <Box mb={3}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link to="..">Build history</Link>
          <Typography>Build details</Typography>
        </Breadcrumbs>
      </Box>

      <Paper className={classes.root}>
        <Tabs
          value={selectedTab}
          onChange={handleChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="Details" />
          <Tab label="Logs" />
        </Tabs>
      </Paper>

      <Box p={2}>
        {selectedTab === 0 && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TableContainer component={Paper} className={classes.table}>
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell>
                        <Typography noWrap>Ref</Typography>
                      </TableCell>
                      <TableCell>{details.value?.substitutions.REF_NAME}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <Typography noWrap>Message</Typography>
                      </TableCell>
                      <TableCell>
                        {details.value?.substitutions.REPO_NAME}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <Typography noWrap>Commit ID</Typography>
                      </TableCell>
                      <TableCell>
                        {details.value?.substitutions.COMMIT_SHA}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <Typography noWrap>Status</Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="end">
                          <WorkflowRunStatus status={details.value?.status} />
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <Typography noWrap>Service Account</Typography>
                      </TableCell>
                      <TableCell>
                        {`${serviceAccount}`}@cloudbuild.gserviceaccount.com
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <Typography noWrap>Build Steps</Typography>
                      </TableCell>
                      <TableCell>
                        <TableContainer className={classes.nestedTable}>
                          <Table size="small" className={classes.stepsTable}>
                            <TableHead>
                              <TableRow>
                                <TableCell className={classes.borderedCell}>
                                  Step
                                </TableCell>
                                <TableCell className={classes.borderedCell}>
                                  ID
                                </TableCell>
                                <TableCell className={classes.borderedCell}>
                                  Status
                                </TableCell>
                                <TableCell className={classes.borderedCell}>
                                  Duration
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {details.value?.steps?.map((step, index) => (
                                <TableRow key={index}>
                                  <TableCell className={classes.borderedCell}>
                                    {index + 1}
                                  </TableCell>
                                  <TableCell className={classes.borderedCell}>
                                    {step.id}
                                  </TableCell>
                                  <TableCell className={classes.borderedCell}>
                                    {step.status}
                                  </TableCell>
                                  <TableCell className={classes.borderedCell}>
                                    {calculateBuildTimeDuration(
                                      step.timing?.startTime,
                                      step.timing?.endTime,
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))}
                              <TableRow>
                                <TableCell
                                  className={classes.borderedCell}
                                  colSpan={3}
                                >
                                  <Typography variant="subtitle2">
                                    Total Build Time
                                  </Typography>
                                </TableCell>
                                <TableCell className={classes.borderedCell}>
                                  <Typography variant="subtitle2">
                                    {totalBuildTime}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <Typography noWrap>Substitutions</Typography>
                      </TableCell>
                      <TableCell>
                        <TableContainer className={classes.nestedTable}>
                          <Table
                            size="small"
                            className={classes.substitutionsTable}
                          >
                            <TableHead>
                              <TableRow>
                                <TableCell className={classes.borderedCell}>
                                  Key
                                </TableCell>
                                <TableCell className={classes.borderedCell}>
                                  Value
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {Object.entries(
                                details.value?.substitutions ?? {},
                              ).map(([key, val]) => (
                                <TableRow key={key}>
                                  <TableCell className={classes.borderedCell}>
                                    {key}
                                  </TableCell>
                                  <TableCell className={classes.borderedCell}>
                                    {val}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <Typography noWrap>Links</Typography>
                      </TableCell>
                      <TableCell>
                        {details.value?.logUrl && (
                          <Link to={details.value.logUrl}>
                            Workflow runs on Google{' '}
                            <ExternalLinkIcon
                              className={classes.externalLinkIcon}
                            />
                          </Link>
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        )}

        {selectedTab === 1 && (
          <Box height="80vh">
            <WorkflowRunLogs
              projectId={projectId}
              location={location}
              status={details.value?.status}
              steps={details.value?.steps}
            />
          </Box>
        )}
      </Box>
    </div>
  );
};
